import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../../core/services/api.service';
import { finalize } from 'rxjs/operators';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';
import { HasPermissionDirective } from '../../../../../../core/auth/directives/has-permission.directive';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface BuyerSnapshot {
  name: string;
  phone: string;
  email: string;
}

interface PaymentPlanProposal {
  downPaymentAmount: number;
  years: number;
  interestRate: number;
}

export interface Offer {
  _id: string;
  targetType: 'unit' | 'property';
  targetId: string;
  buyerId: string;
  ownerId: string;
  offeredPrice: number;
  offerType: 'cash' | 'installment';
  notes: string;
  status: 'pending' | 'reviewing' | 'accepted' | 'rejected' | 'negotiating';
  buyerSnapshot: BuyerSnapshot;
  paymentPlanProposal?: PaymentPlanProposal;
  createdAt: string;
  updatedAt: string;
}

interface OffersResponse {
  data: Offer[];
  pages: number;
  currentPage: number;
  totalItems: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-offers',
  imports: [CommonModule, FormsModule, TranslatePipe, HasPermissionDirective],
  templateUrl: './offers.html',
  styleUrl: './offers.scss',
})
export class Offers implements OnInit {
  offers: Offer[] = [];
  filtered: Offer[] = [];
  isLoading = false;
  error = '';
  totalItems = 0;

  targetType: 'unit' | 'property' = 'unit';
  activeFilter = 'all';

  // Status modal
  showStatusModal = false;
  statusTarget: Offer | null = null;
  newStatus: Offer['status'] = 'pending';
  rejectionReason = '';
  statusLoading = false;

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.error = '';
    this.api
      .get<OffersResponse>(`/offer/target?targetType=${this.targetType}`)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          this.offers = res.data ?? [];
          this.totalItems = res.totalItems ?? 0;
          this.applyFilter();
        },
        error: (err) => (this.error = err?.message || 'Failed to load offers.'),
      });
  }

  switchType(t: 'unit' | 'property'): void {
    this.targetType = t;
    this.load();
  }

  applyFilter(): void {
    this.filtered =
      this.activeFilter === 'all'
        ? this.offers
        : this.offers.filter((o) => o.status === this.activeFilter);
  }

  setFilter(f: string): void {
    this.activeFilter = f;
    this.applyFilter();
  }

  // ── Accept shortcut ─────────────────────────────────────────────────────────

  acceptOffer(id: string): void {
    this.api.patch<{ data: Offer }>(`/offer/accept/${id}`, {}).subscribe({
      next: (res) => {
        const idx = this.offers.findIndex((o) => o._id === id);
        if (idx !== -1) this.offers[idx].status = 'accepted';
        this.applyFilter();
      },
      error: (err) => alert(err?.message || 'Failed to accept offer.'),
    });
  }

  // ── Status modal ────────────────────────────────────────────────────────────

  openStatusModal(o: Offer): void {
    this.statusTarget = o;
    this.newStatus = o.status;
    this.rejectionReason = '';
    this.showStatusModal = true;
  }

  closeStatusModal(): void {
    this.showStatusModal = false;
    this.statusTarget = null;
  }

  saveStatus(): void {
    if (!this.statusTarget) return;
    this.statusLoading = true;
    const body: any = { status: this.newStatus };
    if (this.newStatus === 'rejected') body.rejectionReason = this.rejectionReason;

    this.api
      .patch<{ data: Offer }>(`/offer/status/${this.statusTarget._id}`, body)
      .pipe(finalize(() => (this.statusLoading = false)))
      .subscribe({
        next: (res) => {
          const idx = this.offers.findIndex((o) => o._id === this.statusTarget!._id);
          if (idx !== -1) this.offers[idx].status = res.data.status;
          this.applyFilter();
          this.closeStatusModal();
        },
        error: (err) => alert(err?.message || 'Failed to update status.'),
      });
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  statusBadge(status: string): string {
    const map: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewing: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      negotiating: 'bg-purple-100 text-purple-700',
    };
    return map[status] ?? 'bg-gray-100 text-gray-600';
  }

  offerTypeBadge(type: string): string {
    return type === 'cash' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700';
  }

  fmt(n: number): string {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(0) + 'K';
    return String(n);
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }
}
